import type { Character } from '@/schemas/character';
import { rewindToLevel } from '@/services/characterRewind';
import { levelUpRecordToDraft } from './levelUpRecordToDraft';
import type { LevelUpDraft } from './types';

/**
 * Helper puri per la modalità **edit retroattivo** del LevelUpFlowPage
 * (FI-46 PR-B). Estratti da `LevelUpFlowPage.tsx` per rispettare
 * `max-lines: 400` e `max-lines-per-function: 100`.
 */

/**
 * Risultato di `computeEditModeData`. Tutte le informazioni che il flow
 * page deve conoscere per renderizzarsi in modalità edit.
 */
export interface EditModeData {
  /** PG rewound a fine `L{editLevel - 1}`, pronto a ricevere `applyLevelUp`. */
  characterForFlow: Character;
  /** Draft pre-popolato dal `LevelUpRecord` storico di `L{editLevel}`. */
  initialDraft: LevelUpDraft;
  /**
   * Numero di livelli che verranno **troncati** confermando il nuovo
   * level-up (`originalCharacter.level - editLevel`). Se >0, va mostrato
   * un `ConfirmDialog` di avviso prima di applicare.
   */
  truncatedLevels: number;
}

/**
 * Parsing del param `:level` della route `/character/edit-level-up/:level`.
 * Ritorna `undefined` per parsing fallito (NaN, missing) o non-positivo.
 */
export function parseEditLevel(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed) || parsed < 1) return undefined;
  return parsed;
}

/**
 * Valida che il `:level` sia in range editabile (L2..current.level) e che
 * il `LevelUpRecord` corrispondente esista.
 *
 * **FI-46bis**: il caso `editLevel === 1` è gestito a monte dal page
 * (redirect a `/character/edit-l1` — builder pre-popolato). Qui resta solo
 * la validation per L2+.
 *
 * @returns stringa di errore user-facing, oppure `null` se valido
 */
export function validateEditLevel(editLevel: number, character: Character): string | null {
  if (editLevel < 2) {
    return `Livello "${editLevel}" non valido.`;
  }
  if (editLevel > character.level) {
    return `Il personaggio è al livello ${character.level}: non puoi modificare un livello mai raggiunto.`;
  }
  if (character.levelUpHistory[editLevel - 2] === undefined) {
    return `Nessun record di level-up trovato per L${editLevel}. Il personaggio potrebbe essere stato modificato.`;
  }
  return null;
}

/**
 * Calcola `EditModeData` per il flow in modalità edit. Ritorna `null` se
 * `editLevel` è undefined (modalità "create" — niente edit) o se il record
 * storico non è disponibile (defensive: validato a monte da `validateEditLevel`).
 *
 * **Pure**: usato dentro `useMemo` nel page component.
 */
export function computeEditModeData(
  originalCharacter: Character,
  editLevel: number | undefined,
): EditModeData | null {
  if (editLevel === undefined) return null;
  const recordToReplay = originalCharacter.levelUpHistory[editLevel - 2];
  if (!recordToReplay) return null;
  const characterForFlow = rewindToLevel(originalCharacter, editLevel - 1);
  const initialDraft = levelUpRecordToDraft(recordToReplay, originalCharacter);
  return {
    characterForFlow,
    initialDraft,
    truncatedLevels: originalCharacter.level - editLevel,
  };
}

// `truncateDescription` migrato in `<TruncateLevelsDialog>` molecule shared
// (FI-46bis SSOT) per riuso da edit L1 builder. Esportato da
// `@/components/molecules` se serve fuori dal dialog.
